import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { enqueueSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { queryKeys } from "~/api/queryKeys";
import { isRtl } from "~/helpers/helpers.ts";
import { messageFromError } from "~/helpers/messageFromError";

import { PageMeta } from "../../../api/Api";
import {
  applyPageMeta,
  generateAiInstructionsCustomization,
  generateDescriptionCustomization,
  generateSchemaCustomization,
  generateTitleCustomization,
  getPageMetas,
} from "../../../api/apiFunctions";
import { useAutomationAuthorizations } from "../../../contexts/AutomationAuthorizationContext";
import Button from "../../mantine/button/Button";
import Modal from "../../ui/modal/Modal";
import Spinner from "../../ui/spinner/Spinner";
import contentItemStyles from "../ContentItem.module.scss";
import { ContentItem as ContentItemType } from "../ContentItems";
import MarkdownContent from "../MarkdownContentLazy";
import styles from "./VersionsModal.module.scss";

interface VersionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: ContentItemType;
  group: "enhancement" | "ai" | "schema" | "ai_instructions";
  pageId: number;
  setCurrentItem?: (item: ContentItemType[]) => void;
}

const VersionsModal = ({
  isOpen,
  onClose,
  item,
  group,
  pageId,
}: VersionsModalProps) => {
  const { canGenerateForKind, refetchAuthorizations } =
    useAutomationAuthorizations();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [selectedPageMetaId, setSelectedPageMetaId] = useState<number | null>(
    null,
  );
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set once the user manually picks a version, so the auto-select effect stops
  // overriding their choice while the modal stays open.
  const hasUserSelectedRef = useRef(false);

  const getKind = (): "titles" | "descriptions" | "schemas" | "summary" => {
    if (group === "ai_instructions") return "summary";
    if (group === "schema") return "schemas";
    if (item.title?.toLowerCase().includes("title")) return "titles";
    if (item.title?.toLowerCase().includes("description"))
      return "descriptions";

    return "titles";
  };

  const kind = getKind();
  const siteId = Number(id);

  const {
    data: pageMetasResponse,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.pageMetas(siteId, pageId, kind),
    queryFn: () => getPageMetas(siteId, pageId, kind),
    enabled: isOpen && !!siteId && !!pageId,
  });

  const pageMetas = pageMetasResponse?.page_metas;
  const authorizations = pageMetasResponse?.authorizations;

  // Highlight the applied version (the one carrying the customization) on open
  // and whenever the list refreshes (e.g. after a version was applied from the
  // card), unless the user has manually picked a different one.
  useEffect(() => {
    if (!isOpen || !pageMetas || pageMetas.length === 0) return;
    if (hasUserSelectedRef.current) return;

    const applied =
      pageMetas.find((meta: PageMeta) => !!meta.customization_id) ||
      pageMetas[0];

    setSelectedPageMetaId(applied.id);
  }, [isOpen, pageMetas]);

  const { mutate: applyPageMetaMutation, isPending: isApplying } = useMutation({
    mutationFn: ({ pageMetaId }: { pageMetaId: number }) =>
      applyPageMeta(siteId, pageId, pageMetaId),
    onSuccess: async (_data, { pageMetaId }) => {
      const appliedPageMeta = pageMetas?.find(
        (meta: PageMeta) => meta.id === pageMetaId,
      );
      const versionDate = appliedPageMeta
        ? new Date(appliedPageMeta.created_at).toLocaleDateString()
        : "";

      enqueueSnackbar(`Version from ${versionDate} applied successfully!`, {
        variant: "success",
      });

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.pageMetas(siteId, pageId, kind),
        }),
        queryClient.invalidateQueries({
          queryKey: ["contentAutomations", group, pageId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["widgetCustomizationsStats", group],
        }),
        queryClient.invalidateQueries({
          queryKey: ["customizedPages", group],
        }),
      ]);

      onModalClose();
    },
    onError: (error) => {
      enqueueSnackbar(messageFromError(error), {
        variant: "error",
        persist: true,
      });
    },
  });

  const { mutate: generateNewMutation, isPending: isGenerating } = useMutation({
    mutationFn: () => {
      switch (kind) {
        case "titles":
          return generateTitleCustomization(siteId, pageId, item.id || 0);
        case "descriptions":
          return generateDescriptionCustomization(siteId, pageId, item.id || 0);
        case "schemas":
          return generateSchemaCustomization(siteId, pageId, item.id || 0);
        case "summary":
          return generateAiInstructionsCustomization(
            siteId,
            pageId,
            item.id || 0,
          );
        default:
          throw new Error("Invalid kind");
      }
    },
    onSuccess: async () => {
      enqueueSnackbar("New version generation started!", {
        variant: "success",
      });

      await refetchAuthorizations(pageId, group);

      let pollCount = 0;
      const maxPolls = 30;
      const originalCount = pageMetas?.length || 0;

      const pollForUpdates = () => {
        pollCount++;

        pollTimerRef.current = setTimeout(async () => {
          if (!isOpen) return;

          const result = await refetch();
          const newCount = result.data?.page_metas?.length || 0;

          if (newCount > originalCount) {
            enqueueSnackbar("New version generated successfully!", {
              variant: "success",
            });
            await refetchAuthorizations(pageId, group);
            await Promise.all([
              queryClient.invalidateQueries({
                queryKey: queryKeys.contentAutomationsRoot(group, pageId),
              }),
              queryClient.invalidateQueries({
                queryKey: queryKeys.widgetCustomizationsStatsRoot(group),
              }),
              queryClient.invalidateQueries({
                queryKey: queryKeys.customizedPagesRoot(group),
              }),
            ]);

            return;
          }

          if (pollCount < maxPolls) {
            pollForUpdates();
          }
        }, 2000);
      };

      pollForUpdates();
    },
    onError: (error) => {
      enqueueSnackbar(messageFromError(error), {
        variant: "error",
        persist: true,
      });
    },
  });

  const handleApply = (pageMetaId: number) => {
    applyPageMetaMutation({ pageMetaId });
  };

  const handleGenerate = () => {
    generateNewMutation();
  };
  const canShowGenerateButton = () => {
    if (!pageMetas || pageMetas.length === 0) {
      return false;
    }

    return canGenerateForKind(kind);
  };

  const canApply = (pageMetaId: number) => {
    if (!authorizations || !pageMetas) return false;

    const pageMetaIndex = pageMetas.findIndex(
      (meta: PageMeta) => meta.id === pageMetaId,
    );

    if (pageMetaIndex === -1 || pageMetaIndex >= authorizations.length)
      return false;

    const itemAuthorizations = authorizations[pageMetaIndex] || [];

    return itemAuthorizations.includes("generate_customization");
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const renderContent = (pageMeta: PageMeta) => {
    const hasEditedContent = pageMeta.edited_content;

    // do not for schemas
    const rtl = kind != "schemas" && isRtl(pageMeta.lang);

    // ai.md versions are markdown — render them formatted, like the card body.
    const renderBody = (text: string | null | undefined) =>
      kind === "summary" ? (
        <MarkdownContent content={text || ""} rtl={rtl} lang={pageMeta.lang} />
      ) : (
        <div
          className={clsx(styles.contentText, {
            ["rtl"]: rtl,
          })}
          lang={pageMeta.lang || undefined}
        >
          {text}
        </div>
      );

    return (
      <div className={styles.contentSection}>
        {hasEditedContent && (
          <div className={styles.contentVariant}>
            <div className={styles.contentLabel}>Modified Version:</div>
            {renderBody(pageMeta.edited_content)}
          </div>
        )}
        <div className={styles.contentVariant}>
          <div className={styles.contentLabel}>
            {hasEditedContent ? "Original Version:" : "Content:"}
          </div>
          {renderBody(pageMeta.content)}
        </div>
      </div>
    );
  };

  const onModalClose = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    onClose();
    setSelectedPageMetaId(null);
    hasUserSelectedRef.current = false;
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onModalClose}
      modalLabel={
        <h3 className={styles.modalTitle}>
          Versions for {item.title}
          {item.source && (
            <span
              className={clsx(
                contentItemStyles.sourceBadge,
                item.source === "auto"
                  ? contentItemStyles.auto
                  : contentItemStyles.manual,
              )}
            >
              {item.source === "auto" ? "Auto" : "Manual"}
            </span>
          )}
        </h3>
      }
    >
      <div className={styles.modalContent}>
        {isLoading ? (
          <div className={styles.loading}>
            <Spinner />
          </div>
        ) : (
          <div className={styles.versionsContainer}>
            <div className={styles.versionsHeader}>
              <div className={styles.versionsTitle}>
                Available Versions ({kind})
              </div>
              {canShowGenerateButton() && (
                <Button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  variant="outlined"
                  className={styles.generateButton}
                >
                  {isGenerating && <Spinner size="1rem" />}
                  Generate New
                </Button>
              )}
            </div>

            <div className={styles.versionsList}>
              {pageMetas?.map((pageMeta: PageMeta) => {
                const isSelected = selectedPageMetaId === pageMeta.id;
                const isCurrentlyApplied = false; // TODO: Determine if need this logic or just remove it

                return (
                  <div
                    key={pageMeta.id}
                    className={`${styles.versionItem} ${
                      isSelected ? styles.selected : ""
                    } ${isCurrentlyApplied ? styles.applied : ""}`}
                    onClick={() => {
                      hasUserSelectedRef.current = true;
                      setSelectedPageMetaId(pageMeta.id);
                    }}
                  >
                    <div className={styles.versionHeader}>
                      <div className={styles.versionDate}>
                        {formatDate(pageMeta.created_at)}
                        <span
                          className={clsx(
                            contentItemStyles.sourceBadge,
                            pageMeta.source === "auto"
                              ? contentItemStyles.auto
                              : contentItemStyles.manual,
                          )}
                        >
                          {pageMeta.source === "auto" ? "Auto" : "Manual"}
                        </span>
                      </div>
                      {isCurrentlyApplied && (
                        <div className={styles.appliedBadge}>
                          Currently Applied
                        </div>
                      )}
                      {!isCurrentlyApplied && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApply(pageMeta.id);
                          }}
                          disabled={isApplying || !canApply(pageMeta.id)}
                          variant="outlined"
                          className={styles.applyButton}
                        >
                          {isApplying && <Spinner size="0.8rem" />}
                          {pageMeta.edited_content
                            ? "Restore Original"
                            : "Apply"}
                        </Button>
                      )}
                    </div>

                    {isSelected && renderContent(pageMeta)}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default VersionsModal;
